import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import Switch from 'react-switch';
import Modal from '../modal';
import {
    getScimConfig,
    generateScimToken,
    revokeScimToken,
    setScimActivation,
    getScimAuditLogs,
} from '../../utils/scim-admin-api';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : null);

const SCIMProvisioningSection = ({ updatedClient, data }) => {
    const t = (key) => data.t.read(key);
    const clientId = updatedClient?._id;

    const [config, setConfig] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [newToken, setNewToken] = useState(null);
    const [copied, setCopied] = useState(null); // 'root' | 'token'
    const [confirm, setConfirm] = useState(null); // 'activate' | 'deactivate' | 'revoke'

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        setError(null);
        try {
            const cfg = await getScimConfig(clientId);
            setConfig(cfg);
            // Audit logs are a secondary activity strip — a failure here must
            // not error-banner the whole panel once the config has loaded.
            try {
                const logs = await getScimAuditLogs(clientId, { page_size: 5 });
                setAuditLogs(logs.audit_logs || []);
            } catch (auditError) {
                setAuditLogs([]);
            }
        } catch (e) {
            setError(t('scim_provisioning.load_error'));
        } finally {
            setLoading(false);
        }
    }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        load();
    }, [load]);

    const copyResetTimer = useRef(null);
    useEffect(() => () => clearTimeout(copyResetTimer.current), []);

    const copy = async (text, which) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(which);
            clearTimeout(copyResetTimer.current);
            copyResetTimer.current = setTimeout(() => setCopied(null), 2000);
        } catch (e) {
            /* clipboard unavailable — the value is still selectable in the field */
        }
    };

    const doGenerate = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await generateScimToken(clientId);
            setNewToken(res.token);
            await load();
        } catch (e) {
            setError(t('scim_provisioning.token_error'));
        } finally {
            setBusy(false);
        }
    };

    const doRevoke = async () => {
        setConfirm(null);
        setBusy(true);
        setError(null);
        try {
            await revokeScimToken(clientId);
            setNewToken(null);
            await load();
        } catch (e) {
            setError(t('scim_provisioning.token_error'));
        } finally {
            setBusy(false);
        }
    };

    const doSetActivation = async (enabled) => {
        setConfirm(null);
        setBusy(true);
        setError(null);
        try {
            await setScimActivation(clientId, enabled);
            await load();
        } catch (e) {
            setError(t('scim_provisioning.activation_error'));
        } finally {
            setBusy(false);
        }
    };

    const eventLabel = (type) => {
        const key = `scim_provisioning.event.${type}`;
        const label = t(key);
        return label === key ? type : label;
    };

    const copyButton = (text, which) => (
        <button
            type="button"
            className="btn btn-outline-secondary"
            title={t('scim_provisioning.copy')}
            onClick={() => copy(text, which)}
        >
            <FontAwesomeIcon icon={copied === which ? faCheck : faCopy} />
        </button>
    );

    if (!clientId) {
        return (
            <div className="mb-2">
                <h5 className="mt-4">{t('scim_provisioning.title')}</h5>
                <p className="text-muted">{t('scim_provisioning.save_client_first')}</p>
            </div>
        );
    }

    const activationEnabled = !!config?.activation?.enabled;

    return (
        <div className="mb-2">
            <h5 className="mt-4">{t('scim_provisioning.title')}</h5>
            <p className="text-muted">{t('scim_provisioning.description')}</p>

            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading && (
                <i>{t('please_wait')}...</i>
            )}

            {config && !loading && (
                <>
                    {/* Activation — the consequential switch, shown first */}
                    <div className="mt-2">
                        <h6 className="mb-2">{t('scim_provisioning.activation')}</h6>
                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                            <Switch
                                className="switch"
                                checked={activationEnabled}
                                disabled={busy}
                                width={70}
                                onColor="#F39800"
                                onHandleColor="#2693e6"
                                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                onChange={() => setConfirm(activationEnabled ? 'deactivate' : 'activate')}
                            />
                            <span className="mb-0">
                                {activationEnabled
                                    ? t('scim_provisioning.activation_on')
                                    : t('scim_provisioning.activation_off')}
                            </span>
                        </div>
                        {activationEnabled && (
                            <div className="alert alert-info py-2 mt-2 mb-0">
                                {t('scim_provisioning.not_yet_enforced')}
                            </div>
                        )}
                        {config.activation?.updated_at && (
                            <small className="text-muted">
                                {t('scim_provisioning.activation_updated')} {fmtDate(config.activation.updated_at)}
                                {config.activation.updated_by?.username && (
                                    <> — {config.activation.updated_by.username}</>
                                )}
                            </small>
                        )}
                    </div>

                    {/* Endpoint root — paste into the identity provider */}
                    <div className="mt-3">
                        <h6 className="mb-2">{t('scim_provisioning.endpoint_root')}</h6>
                        <div className="input-group">
                            <input className="form-control" readOnly value={config.endpoint_root || ''} />
                            <div className="input-group-append">{copyButton(config.endpoint_root || '', 'root')}</div>
                        </div>
                        <small className="text-muted">{t('scim_provisioning.endpoint_root_help')}</small>
                    </div>

                    {/* Bearer token lifecycle */}
                    <div className="mt-3">
                        <h6 className="mb-2">{t('scim_provisioning.bearer_token')}</h6>

                        {newToken ? (
                            <div className="alert alert-warning">
                                <div className="mb-2">{t('scim_provisioning.token_shown_once')}</div>
                                <div className="input-group">
                                    <input className="form-control" readOnly value={newToken} />
                                    <div className="input-group-append">{copyButton(newToken, 'token')}</div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-dark mt-2"
                                    onClick={() => setNewToken(null)}
                                >
                                    {t('scim_provisioning.token_done')}
                                </button>
                            </div>
                        ) : (
                            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                {config.token_configured ? (
                                    <>
                                        <span className="text-muted">
                                            {t('scim_provisioning.token_configured')}
                                            {config.token_last_rotated_at && (
                                                <> — {fmtDate(config.token_last_rotated_at)}</>
                                            )}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={doGenerate}>
                                            {t('scim_provisioning.rotate_token')}
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => setConfirm('revoke')}>
                                            {t('scim_provisioning.revoke_token')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-muted">{t('scim_provisioning.token_not_configured')}</span>
                                        <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={doGenerate}>
                                            {t('scim_provisioning.generate_token')}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Last push from the identity provider */}
                    <div className="mt-3">
                        <h6 className="mb-2">{t('scim_provisioning.last_push')}</h6>
                        {config.last_push_at ? (
                            <span className="text-muted">
                                {fmtDate(config.last_push_at)}
                                {config.last_push_status && (
                                    <span className={`badge ml-2 ${config.last_push_status === 'ok' ? 'badge-success' : 'badge-danger'}`}>
                                        {config.last_push_status}
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="text-muted">{t('scim_provisioning.no_push_yet')}</span>
                        )}
                    </div>

                    {/* Recent activity (audit log channel) */}
                    {auditLogs.length > 0 && (
                        <div className="mt-3">
                            <h6 className="mb-2">{t('scim_provisioning.recent_activity')}</h6>
                            <ul className="list-unstyled mb-0">
                                {auditLogs.map((entry) => (
                                    <li key={entry.id} className="text-muted small">
                                        {fmtDate(entry.created_at)} — {eventLabel(entry.event_type)}
                                        {entry.actor?.username && <> ({entry.actor.username})</>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {confirm === 'revoke' && (
                <Modal
                    centered
                    title={t('scim_provisioning.revoke_confirm_title')}
                    okAction={doRevoke}
                    cancelAction={() => setConfirm(null)}
                    okText={t('scim_provisioning.revoke_token')}
                    cancelText={t('cancel')}
                >
                    <p>{t('scim_provisioning.revoke_confirm_body')}</p>
                </Modal>
            )}

            {(confirm === 'activate' || confirm === 'deactivate') && (
                <Modal
                    centered
                    title={t('scim_provisioning.activation_confirm_title')}
                    okAction={() => doSetActivation(confirm === 'activate')}
                    cancelAction={() => setConfirm(null)}
                    okText={t('confirm')}
                    cancelText={t('cancel')}
                >
                    <p>{confirm === 'activate'
                        ? t('scim_provisioning.activate_confirm_body')
                        : t('scim_provisioning.deactivate_confirm_body')}</p>
                </Modal>
            )}
        </div>
    );
};

export default SCIMProvisioningSection;
